import type VideoPlayer from '../../types/VideoPlayer'
import { VideoEvent, VideoType } from '../constants/enums'
import {
    VideoConfig,
    generateVideoConfig,
} from '../mfcLogic/generateVideoConfig'
import { pubSubVideo } from './pubSub'
import { SnapSize, registerSnapTimer } from './state.timers'
import { USERSSTATE, getSlugAndId, slugToID } from './state.users'

export type SnapVideoState = {
    [id: number]: {
        id: number
        container: HTMLElement
        instance: VideoPlayer.Instance
        config: VideoConfig
        playing: boolean
        videoID: VideoType
        videoReadyCallback: (playing: boolean) => void
    }
}

export const SNAPVIDEOSTATE: SnapVideoState = {}

if (typeof window !== 'undefined') {
    window.__MFC_APP_SNAP_VIDEO__ = SNAPVIDEOSTATE
}

export const startVideo = (idOrSlug: string | number) => {
    const [_, id] = getSlugAndId(idOrSlug)
    const player = SNAPVIDEOSTATE[id]
    player?.instance?.render({ append: false })
    SNAPVIDEOSTATE[id] = { ...player }
    pubSubVideo.emit(VideoEvent.SNAP + '::' + id, SNAPVIDEOSTATE[id])
}

export const setupSnapVideo = (
    idOrSlug: string | number,
    options: {
        videoID: VideoType
        container: HTMLElement
        hideControls?: boolean
        forceMute?: boolean
        autoplayMutedOverlay?: boolean
        onlyVolumeControls?: boolean
        force?: boolean
    },
    videoReadyCallback: (playing: boolean) => void,
    toggleFullscreenCallback?: () => void,
): boolean => {
    const {
        videoID,
        container,
        hideControls = false,
        onlyVolumeControls = false,
        forceMute = true,
        force = false,
    } = options
    const [slug, id] = getSlugAndId(idOrSlug)
    const player = SNAPVIDEOSTATE[id]
    registerSnapTimer(id, SnapSize.large)
    // check and destroy existing only if it's a lower priority type
    // -- Menu > Room > PinnedPm > PinnedRoom > FloatingPreview > ModelTile
    if (!destroySnapVideo(id, videoID, force)) {
        // we ran into a lower priority video wanting to play and no force override
        // console.log('[setupSnapVideo] higher priority video already playing', {
        //     id,
        //     videoID,
        //     force,
        // })
        return false
    }

    const config = generateVideoConfig(id, window.VideoPlayer)
    if (!config) {
        console.error(
            '[setupSnapVideo] failed to generate video config',
            {
                idOrSlug,
                id,
                slug,
                player,
            },
            { ...USERSSTATE[slugToID['me']] },
        )
        return false
    }
    config.capLevelToPlayerSize = true
    config.allowControls = !hideControls
    config.videoId = 'fvideo_ff_' + videoID + '_' + slug
    config.mute = forceMute
    config.containerEl = container
    config.forceAutoPlay = true
    config.onlyVolumeControls = onlyVolumeControls
    // config.autoplayMuted = autoplayMuted
    config.autoplayMutedOverlay = false // autoplayMutedOverlay

    const instance = new window.VideoPlayer(config)
    if (!forceMute) {
        instance.setMute(false)
        instance.setVolume(instance.volumeByModel() || 1)
    }
    const newPlayerState = {
        id,
        config,
        container,
        instance,
        videoID,
        videoReadyCallback,
    }
    SNAPVIDEOSTATE[id] = { ...player, ...newPlayerState }
    pubSubVideo.emit(VideoEvent.SNAP + '::' + id, SNAPVIDEOSTATE[id])
    instance.setOnPlayingCallback(() => {
        try {
            const player = SNAPVIDEOSTATE[id]
            if (!player?.instance) return
            player.playing = player.instance.playing
            videoReadyCallback(player.instance.playing)
        } catch (e) {
            console.error('[SnapVideo]', { e, videoID, id, slug })
        }
    })
    instance.setToggleFullscreenHandler(() => {
        toggleFullscreenCallback()
        // if we set this handler, then we're responsible for it all, so we
        // have to trigger the full screen request that VideoPlayer normally does
        instance.player._ORIG_requestFullscreen()
    })
    return true
}

const destroySnapVideo = (id: number, videoID: VideoType, force = false) => {
    const player = SNAPVIDEOSTATE[id]
    const playerExists = !!(player && player.instance)
    if (!playerExists) return true
    if (player.videoID <= videoID || force) {
        player.videoReadyCallback(false)
        player.instance.Disconnect()
        player.instance = null
        player.container = null
        // console.log('[destroySnapVideo] destroyed', { id, videoID })
        delete SNAPVIDEOSTATE[id]
        return true
    }

    // unsafe to proceed with starting new video
    return false
}

export const unregisterSnapVideo = (
    idOrSlug: string | number,
    videoID: VideoType,
) => {
    const [_, id] = getSlugAndId(idOrSlug)
    destroySnapVideo(id, videoID)
}
